import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/HomePage";
import DevelopSalesPage from "@/pages/DevelopSalesPage";
import LoyaltyPage from "@/pages/LoyaltyPage";
import ShopPage from "@/pages/ShopPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import FormationsPage from "@/pages/FormationsPage";
import ContactPage from "@/pages/ContactPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AdminPage from "@/pages/AdminPage";
import ProfilePage from "@/pages/ProfilePage";
import StrategyPage from "@/pages/StrategyPage";
import PodcastPage from "@/pages/PodcastPage";

// Components
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Cart Context
export const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("moreati_token"));
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [cartOpen, setCartOpen] = useState(false);

  // Sync token with localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem("moreati_token");
    if (storedToken && storedToken !== token) {
      setToken(storedToken);
    }
  }, []);

  // Initialize data
  useEffect(() => {
    const init = async () => {
      try {
        // Seed data
        await axios.post(`${API}/seed`).catch(() => {});
      } catch (e) {
        // Seed already done
      }

      // Check auth
      const currentToken = localStorage.getItem("moreati_token");
      if (currentToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${currentToken}` }
          });
          setUser(response.data);
          setToken(currentToken);
          // Fetch cart
          fetchCart(currentToken);
        } catch (e) {
          console.log("Token invalid:", e);
          localStorage.removeItem("moreati_token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  const fetchCart = async (authToken) => {
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setCart(response.data);
    } catch (e) {
      console.log("Cart fetch error:", e);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("moreati_token", access_token);
    setToken(access_token);
    setUser(userData);
    fetchCart(access_token);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem("moreati_token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("moreati_token");
    setToken(null);
    setUser(null);
    setCart({ items: [], total: 0 });
  };

  const addToCart = async (productId, quantity = 1, customization = null) => {
    if (!token) {
      return { error: "Please login to add items to cart" };
    }
    try {
      const response = await axios.post(
        `${API}/cart/add`,
        { product_id: productId, quantity, customization },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCart(response.data);
      setCartOpen(true);
      return response.data;
    } catch (e) {
      console.error("Add to cart error:", e);
      return { error: e.response?.data?.detail || "Error adding to cart" };
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    try {
      const response = await axios.put(
        `${API}/cart/update/${itemId}?quantity=${quantity}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCart(response.data);
      return response.data;
    } catch (e) {
      console.error("Update cart error:", e);
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      const response = await axios.delete(`${API}/cart/remove/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
      return response.data;
    } catch (e) {
      console.error("Remove from cart error:", e);
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart({ items: [], total: 0 });
    } catch (e) {
      console.error("Clear cart error:", e);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#C65D47] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[#1D3557] font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout }}>
      <CartContext.Provider
        value={{
          cart,
          cartOpen,
          setCartOpen,
          addToCart,
          updateCartItem,
          removeFromCart,
          clearCart,
          refreshCart: () => fetchCart(token)
        }}
      >
        <BrowserRouter>
          <div className="App min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/piloter-strategie" element={<StrategyPage />} />
                <Route path="/developper-ventes" element={<DevelopSalesPage />} />
                <Route path="/fideliser-clients" element={<LoyaltyPage />} />
                <Route path="/podcast" element={<PodcastPage />} />
                <Route path="/boutique" element={<ShopPage />} />
                <Route path="/boutique/:productId" element={<ProductDetailPage />} />
                <Route path="/panier" element={<CartPage />} />
                <Route path="/checkout" element={token ? <CheckoutPage /> : <Navigate to="/login" />} />
                <Route path="/formations" element={<FormationsPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" />} />
                <Route path="/register" element={!user ? <RegisterPage /> : <Navigate to="/" />} />
                <Route path="/profile" element={token ? <ProfilePage /> : <Navigate to="/login" />} />
                <Route
                  path="/admin/*"
                  element={user?.is_admin ? <AdminPage /> : <Navigate to="/" />}
                />
              </Routes>
            </main>
            <Footer />
            <CartDrawer />
            <Toaster position="top-right" richColors />
          </div>
        </BrowserRouter>
      </CartContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;
